import { z } from 'zod'
import { useForm, useWatch } from 'react-hook-form'
import { toast } from 'react-toastify'
import { parseEther, formatEther, type Hex } from 'viem'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCurrencyFormatter } from '@/hooks/helpers/useCurrencyFormatter'
import { Button } from '@/components/ui/Button'
import { Skeleton } from '@/components/ui/Skeleton'
import { AmountForm } from './AmountForm'
import { logger } from '@/observability/logger'
import Info from '@/assets/icons/Info.svg?react'
import { minBigInt } from '@/utils/math/bigint'
import {
	ResponsiveDialog,
	ResponsiveDialogContent,
	ResponsiveDialogFooter,
	ResponsiveDialogHeader,
	ResponsiveDialogTitle,
	type ResponsiveDialogProps
} from '@/components/ui/ResponsiveDialog'
import { testIdFactory } from '@/utils/test-id-factory.ts'
import { useTracking } from '@/providers/TrackingProvider'

const stakingFormSchema = (totalStake: bigint, votingCap: bigint) =>
	z.object({
		amount: z
			.string()
			.regex(/^\d*\.?\d*$/, 'Invalid number')
			.refine(x => Number(x) >= 1, 'Must be at least 1 PEPPER')
			.refine(x => Number(x) <= Number(formatEther(votingCap)), "Amount exceeds the poll's voting cap")
			.refine(x => Number(x) <= Number(formatEther(totalStake)), 'Amount exceeds your available staked PEPPER')
	})

export function VoteDialog({
	selectedOption,
	userUnlockedTokens,
	userLockedTokens,
	isLoading = false,
	onSubmit,
	votingCap,
	onVote,
	...dialogProps
}: Omit<ResponsiveDialogProps, 'children'> & {
	selectedOption: string
	userUnlockedTokens: bigint
	userLockedTokens: bigint
	votingCap: bigint
	isLoading?: boolean
	onSubmit: () => void
	onVote: (selectedOption: string, amountInWei: bigint) => Promise<Hex | undefined>
}) {
	// Hooks and State
	const format = useCurrencyFormatter(3)
	const totalStake = userUnlockedTokens + userLockedTokens
	const form = useForm({
		mode: 'onChange',
		resolver: zodResolver(stakingFormSchema(totalStake, votingCap)),
		defaultValues: {
			amount: formatEther(minBigInt(votingCap, totalStake) ?? 0n)
		}
	})
	const { sendEvent } = useTracking()
	const amount = useWatch({ control: form.control, name: 'amount' })
	const amountInWei = amount ? parseEther(amount) : 0n

	const handleVote = form.handleSubmit(async () => {
		try {
			sendEvent('click_vote')
			const hash = await onVote(selectedOption, amountInWei)

			// Temp fix - Should be handled properly
			if (!hash) {
				return
			}

			sendEvent('vote_successful', { answer: selectedOption, amount, txHash: hash })
			await onSubmit()
		} catch (e) {
			logger.error(e, 'Vote error')
			toast.error(
				`An error occurred while staking Pepper for selected option ${selectedOption} and amountWei ${amountInWei}`
			)
		}
	})

	return (
		<ResponsiveDialog {...dialogProps}>
			<ResponsiveDialogContent>
				<div className="overflow-y-auto gap-8 flex flex-col">
					<ResponsiveDialogHeader>
						<ResponsiveDialogTitle className="uppercase">
							How much Pepper do you want to lock?
						</ResponsiveDialogTitle>
					</ResponsiveDialogHeader>
					<div className="flex gap-2 items-start md:items-center text-neutral-30">
						<Info className="w-[2.5rem] md:w-[1.5rem]" />
						<span className="text-2xl leading-none">
							{`The poll's voting cap is ${format(formatEther(votingCap))} PEPPER.`}
						</span>
					</div>
					<div>
						<AmountForm form={form} base={minBigInt(votingCap, totalStake) ?? 0n} />
					</div>
					<h4 className="text-center text-3xl text-neutral-40 leading-none flex items-center justify-center gap-2 flex-col md:flex-row">
						<span>Available Staked Balance:</span>
						{isLoading ? (
							<Skeleton className="w-[13ch] h-[30px]" />
						) : (
							<span>{format(formatEther(totalStake))}</span>
						)}
					</h4>
					<ResponsiveDialogFooter>
						<Button
							onClick={handleVote}
							disabled={!form.formState.isValid || !amount}
							loading={form.formState.isSubmitting}
							variant="danger"
							className="py-5 px-10 leading-none"
							data-testid={testIdFactory.pepperAmountFormButton('vote')}
						>
							{'Vote'}
						</Button>
					</ResponsiveDialogFooter>
				</div>
			</ResponsiveDialogContent>
		</ResponsiveDialog>
	)
}
